import React, { useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { BookMarked, Plus, Settings, Sparkles, UserRound } from "lucide-react";
import { useLingui } from "@lingui/react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { UserMenu } from "@/components/layout/UserMenu";
import { useOrganisation } from "@/hooks/settings/useOrganisation";
import { useImageUrl } from "@/hooks/useImage";
import { useNavBadges } from "@/hooks/useNavBadges";
import { useAccess } from "@/hooks/useAccess";
import { canUseMePortal } from "@/components/me/mePortal";
import { useWhatsNewUnseen } from "@/guide/whatsNew";
import { ROUTE_MODULES, routeModules, type RouteAccess } from "@/lib/modules";
import { NAV_SECTIONS, isUnderPath, matchNavItem, navItemLabel, type NavBadgeKey } from "@/lib/navigation";
import { isDirectImageUrl, isImageId } from "@/utils/imageUtils";

/** The access rule of a sidebar link; a link without a route entry is admin-only. */
function routeAccessOf(href: string): RouteAccess {
  return ROUTE_MODULES[href] ?? 'admin';
}

/** A count as the badge shows it: nothing at zero, "99+" above 99. */
function badgeText(count: number | undefined): string | undefined {
  if (!count || count <= 0) return undefined;
  return count > 99 ? '99+' : String(count);
}

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useLingui();
  const organisation = useOrganisation().data;

  // The organisation logo is an image id (resolved to a URL) or a direct URL.
  const logoValue = organisation?.logo_url;
  const needsImageUrlResolution = isImageId(logoValue);
  const resolvedImageUrl = useImageUrl(needsImageUrlResolution ? logoValue : null);
  let logo_url: string | undefined;
  if (needsImageUrlResolution && resolvedImageUrl) {
    logo_url = resolvedImageUrl;
  } else if (isDirectImageUrl(logoValue)) {
    logo_url = logoValue;
  }

  // Module filter (issue #1670): an employee sees only the items of the modules
  // their role holds, so no employee lands on a 403 page. An admin sees every
  // item. The server module gates are the boundary; this is UX.
  const { isAdmin, hasModule } = useAccess();
  const canSee = React.useCallback(
    (href: string) => {
      const access = routeAccessOf(href);
      return isAdmin || (access !== 'admin' && routeModules(access).some(hasModule));
    },
    [isAdmin, hasModule],
  );

  // Badge counts run only when the session may open the page (module gated in the hook).
  const navBadges = useNavBadges();
  const badges: Partial<Record<NavBadgeKey, string | undefined>> = useMemo(
    () => ({
      approvals: badgeText(navBadges.approvals.count),
      messages: badgeText(navBadges.messages.count),
      absences: badgeText(navBadges.absences.count),
      timeEntries: badgeText(navBadges.timeEntries.count),
      overdueInvoices: badgeText(navBadges.overdueInvoices.count),
    }),
    [navBadges],
  );

  const sections = useMemo(
    () =>
      NAV_SECTIONS.map((section) => ({
        ...section,
        items: section.items.filter((item) => canSee(item.href)),
      })).filter((section) => section.items.length > 0),
    [canSee],
  );

  const showMyAccount = canUseMePortal({ isAdmin, hasModule });
  // The longest matching item only: /children/consents lies under /children too.
  const activeHref = matchNavItem(location.pathname)?.item.href;
  const onSettings = isUnderPath(location.pathname, '/settings');
  const settingsLabel = i18n._({ id: 'settings.title', message: 'Settings' });
  const myAccountLabel = i18n._({ id: 'mePortal.userMenu.myAccount', message: 'My account' });
  const guideLabel = i18n._({ id: 'guide.title', message: 'Guide' });
  const onGuide = isUnderPath(location.pathname, '/guide');
  const whatsNewLabel = i18n._({ id: 'whatsNew.title', message: "What's new" });
  const onWhatsNew = isUnderPath(location.pathname, '/whats-new');
  const whatsNewUnseen = useWhatsNewUnseen();

  return (
    <Sidebar variant="inset" collapsible="icon" className="!border-r-0">
      <SidebarHeader className="p-4 [@media(max-height:820px)]:p-3">
        {/* The logo goes to "/": the route guard sends an employee on to the first granted module. */}
        <Link to="/" className="flex items-center space-x-2 group-data-[collapsible=icon]:justify-center no-underline">
          <Avatar className="h-8 w-8 flex-shrink-0">
            {logo_url && <AvatarImage src={logo_url} alt="" />}
            <AvatarFallback className="bg-primary text-primary-foreground font-bold">
              {organisation?.name?.split(' ').map((word) => word[0]).join('').toUpperCase() || 'ORG'}
            </AvatarFallback>
          </Avatar>
          <span className="group-data-[collapsible=icon]:hidden truncate font-semibold text-sm text-sidebar-foreground">
            {organisation?.name || i18n._({ id: 'nav.organizationDefault', message: 'Organization' })}
          </span>
        </Link>
      </SidebarHeader>

      <SidebarContent>
        {sections.map((section) => (
          <SidebarGroup key={section.key} className="[@media(max-height:820px)]:py-1">
            {section.label && (
              <SidebarGroupLabel className="text-sidebar-foreground/70 [@media(max-height:820px)]:h-6">
                {i18n._(section.label)}
              </SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu>
                {section.items.map((item) => {
                  const title = i18n._(navItemLabel(item));
                  const isActive = item.href === activeHref;
                  const badge = item.badge ? badges[item.badge] : undefined;
                  return (
                    <SidebarMenuItem key={item.href}>
                      <SidebarMenuButton asChild isActive={isActive} tooltip={title} className="[@media(max-height:820px)]:h-7">
                        <Link to={item.href} title={title} aria-current={isActive ? 'page' : undefined} className="relative">
                          <item.icon />
                          <span className="truncate">{title}</span>
                          {badge && (
                            <>
                              <span
                                className="ml-auto rounded-full bg-destructive px-1.5 py-0.5 text-center text-xs font-medium text-destructive-foreground min-w-[1.25rem] group-data-[collapsible=icon]:hidden"
                                aria-hidden="true"
                                data-testid={item.href === '/messaging' ? 'sidebar-unread-badge' : undefined}
                              >
                                {badge}
                              </span>
                              {/* Collapsed rail: a dot keeps the signal without the number. */}
                              <span
                                className="absolute right-1 top-1 hidden h-2 w-2 rounded-full bg-destructive group-data-[collapsible=icon]:block"
                                aria-hidden="true"
                              />
                              <span className="sr-only">
                                {i18n._({ id: 'nav.badge.openItems', message: '{count} open' }, { count: badge })}
                              </span>
                            </>
                          )}
                        </Link>
                      </SidebarMenuButton>
                      {item.addHref && (
                        <SidebarMenuAction
                          className="peer-data-[active=true]/menu-button:opacity-100"
                          onClick={() => navigate(item.addHref as string)}
                        >
                          <Plus />
                          <span className="sr-only">
                            {i18n._({ id: 'nav.addItem', message: 'Add: {title}' }, { title })}
                          </span>
                        </SidebarMenuAction>
                      )}
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter className="py-3">
        <SidebarMenu>
          {/* Settings are admin-only (issue #1670). */}
          {isAdmin && (
            <SidebarMenuItem className="mb-0.5">
              <SidebarMenuButton asChild isActive={onSettings} tooltip={settingsLabel}>
                <Link to="/settings" title={settingsLabel} aria-current={onSettings ? 'page' : undefined}>
                  <Settings />
                  <span>{settingsLabel}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          {/* "My account" opens the /me self-service portal (#1768): own shifts, absences, hours. */}
          {showMyAccount && (
            <SidebarMenuItem className="mb-0.5">
              <SidebarMenuButton asChild tooltip={myAccountLabel}>
                <Link to="/me" title={myAccountLabel}>
                  <UserRound />
                  <span>{myAccountLabel}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          {/* The platform guide (#2059): every user, each sees the topics of their own areas. */}
          <SidebarMenuItem className="mb-0.5">
            <SidebarMenuButton asChild isActive={onGuide} tooltip={guideLabel}>
              <Link to="/guide" title={guideLabel} aria-current={onGuide ? 'page' : undefined}>
                <BookMarked />
                <span>{guideLabel}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          {/* What's new (#2109): a dot until this browser opened the latest release notes. */}
          <SidebarMenuItem className="mb-0.5">
            <SidebarMenuButton asChild isActive={onWhatsNew} tooltip={whatsNewLabel}>
              <Link to="/whats-new" title={whatsNewLabel} aria-current={onWhatsNew ? 'page' : undefined} className="relative">
                <Sparkles />
                <span>{whatsNewLabel}</span>
                {whatsNewUnseen && (
                  <>
                    <span
                      className="ml-auto h-2 w-2 shrink-0 rounded-full bg-primary group-data-[collapsible=icon]:hidden"
                      aria-hidden="true"
                      data-testid="whats-new-dot"
                    />
                    {/* Collapsed rail: the same dot on the icon. */}
                    <span
                      className="absolute right-1 top-1 hidden h-2 w-2 rounded-full bg-primary group-data-[collapsible=icon]:block"
                      aria-hidden="true"
                      data-testid="whats-new-dot"
                    />
                    <span className="sr-only">
                      {i18n._({ id: 'whatsNew.badge.unseen', message: 'New release notes' })}
                    </span>
                  </>
                )}
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <UserMenu showMyAccount={showMyAccount} showNotifications={isAdmin} />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
